import {
  BIndexThresholdType,
  QBankStructureStatus,
  QuestionStatus,
  useGetAllQBankStructuresQuery,
} from '@report/graphql';
import { ItemAnalysisOtherFilterFormField } from '@report/qbank-item-analysis';
import { AVAILABLE_YEARS_OPTIONS } from '@report/utils';
import {
  Form,
  Flex,
  Divider,
  Button,
  FormSearchSelection,
  Box,
  Text,
  Grid,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
const QuestionStatusOptions = [
  { label: 'Published', value: QuestionStatus.Published },
  { label: 'Disabled', value: QuestionStatus.Disabled },
];

import { MONTH_OPTIONS } from '../../constants';
import { useItemAnalysisYearlySearchParams } from '../../hooks';

import {
  type ItemAnalysisYearlyFilterFormInput,
  itemAnalysisYearlyFilterFormSchema,
} from './schema';

export default function ItemAnalysisFilterYearlySection() {
  const [
    {
      page,
      pageSize,
      startMonth,
      endMonth,
      startYear,
      endYear,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
    },
    { changeFilterParams },
  ] = useItemAnalysisYearlySearchParams();

  const { data: qBankStructuresData, loading: getAllQBankStructuresLoading } =
    useGetAllQBankStructuresQuery({
      variables: {
        getAllQBankStructuresInput: {
          statuses: [
            QBankStructureStatus.Published,
            QBankStructureStatus.Unpublished,
            QBankStructureStatus.Archived,
          ],
        },
      },
    });

  const qBankStructuresOptions = useMemo(
    () =>
      qBankStructuresData?.reportGetAllQBankStructures.qBankStructures.map((structure) => ({
        label: structure.name,
        value: structure.id,
      })) ?? [],
    [qBankStructuresData],
  );

  const selectedStartMonth = MONTH_OPTIONS.find((month) => month.value === startMonth);
  const selectedEndMonth = MONTH_OPTIONS.find((month) => month.value === endMonth);
  const selectedStartYear = AVAILABLE_YEARS_OPTIONS.find((year) => year.value === startYear);
  const selectedEndYear = AVAILABLE_YEARS_OPTIONS.find((year) => year.value === endYear);

  const formContext = useForm<ItemAnalysisYearlyFilterFormInput>({
    defaultValues: {
      startMonth: selectedStartMonth ?? undefined,
      startYear: selectedStartYear ?? undefined,
      endMonth: selectedEndMonth ?? undefined,
      endYear: selectedEndYear ?? undefined,
      questionStatus: questionStatus.map((status) => ({
        label: QuestionStatusOptions.find((option) => option.value === status)?.label ?? '',
        value: status,
      })),
      minExaminees: minExaminees ?? undefined,
      bIndex: {
        knowledgeGroup: {
          knowledgeGroupSelection: knowledgeGroupSelection ?? BIndexThresholdType.UseExamResult,
          customThreshold: knowledgeGroupCustomThreshold ?? undefined,
        },
        nonKnowledgeGroup: {
          nonKnowledgeGroupSelection:
            nonKnowledgeGroupSelection ?? BIndexThresholdType.UseExamResult,
          customThreshold: nonKnowledgeGroupCustomThreshold ?? undefined,
        },
      },
    },
    resolver: zodResolver(itemAnalysisYearlyFilterFormSchema),
  });

  useEffect(() => {
    formContext.reset({
      ...formContext.getValues(),
      examCourse: examCourse
        ? {
            label:
              qBankStructuresOptions.find((option) => option.value === examCourse)?.label ?? '',
            value: examCourse,
          }
        : undefined,
    });
  }, [examCourse, formContext, qBankStructuresOptions]);
  const { watch, resetField } = formContext;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'bIndex.knowledgeGroup.knowledgeGroupSelection') {
        resetField('bIndex.knowledgeGroup.customThreshold', {
          defaultValue: undefined,
        });
      }
      if (name === 'bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection') {
        resetField('bIndex.nonKnowledgeGroup.customThreshold', {
          defaultValue: undefined,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, resetField]);

  const { bIndex } = watch();

  const disableKnowledgeGroupCustomThreshold =
    bIndex.knowledgeGroup.knowledgeGroupSelection !== BIndexThresholdType.CustomThreshold;
  const disableNonKnowledgeGroupCustomThreshold =
    bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection !== BIndexThresholdType.CustomThreshold;

  const onSubmit = (data: ItemAnalysisYearlyFilterFormInput) => {
    const {
      startMonth,
      startYear,
      endMonth,
      endYear,
      examCourse,
      questionStatus,
      minExaminees,
      bIndex,
    } = data;
    changeFilterParams({
      startMonth: startMonth?.value ?? '',
      startYear: startYear?.value ?? 0,
      endMonth: endMonth?.value ?? '',
      endYear: endYear?.value ?? 0,
      examCourse: examCourse.value ?? '',
      questionStatus: questionStatus.map((item) => {
        return item.value;
      }),
      minExaminees,
      knowledgeGroupSelection: bIndex.knowledgeGroup.knowledgeGroupSelection,
      knowledgeGroupCustomThreshold:
        bIndex.knowledgeGroup.knowledgeGroupSelection === BIndexThresholdType.CustomThreshold
          ? (bIndex.knowledgeGroup.customThreshold ?? 0)
          : null,
      nonKnowledgeGroupSelection: bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold:
        bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection === BIndexThresholdType.CustomThreshold
          ? (bIndex.nonKnowledgeGroup.customThreshold ?? 0)
          : null,
      page: page,
      pageSize: pageSize,
    });
  };

  const handleReset = () => {
    formContext.reset({
      startMonth: undefined,
      startYear: undefined,
      endMonth: undefined,
      endYear: undefined,
      examCourse: undefined,
      questionStatus: [],
      minExaminees: undefined,
      bIndex: {
        knowledgeGroup: {
          knowledgeGroupSelection: BIndexThresholdType.UseExamResult,
          customThreshold: undefined,
        },
        nonKnowledgeGroup: {
          nonKnowledgeGroupSelection: BIndexThresholdType.UseExamResult,
          customThreshold: undefined,
        },
      },
    });
    changeFilterParams({
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      examCourse: '',
      questionStatus: [],
      minExaminees: null,
      knowledgeGroupSelection: BIndexThresholdType.UseExamResult,
      knowledgeGroupCustomThreshold: null,
      nonKnowledgeGroupSelection: BIndexThresholdType.UseExamResult,
      nonKnowledgeGroupCustomThreshold: null,
      page: 0,
      pageSize: 50,
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(onSubmit)}
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
      >
        <Grid
          sx={(theme) => ({
            columnGap: theme.spacing(6),
            rowGap: theme.spacing(6),
            gridTemplateColumns: '160px 1fr',
          })}
        >
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Date Filter:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(2),
                alignItems: 'flex-start',
                flexBasis: '408px',
              })}
            >
              <Box sx={{ flex: 1 }}>
                <FormSearchSelection
                  name='startMonth'
                  label='Start Month'
                  placeholder='เลือก Month'
                  options={MONTH_OPTIONS}
                />
              </Box>
              <Box sx={(theme) => ({ flex: 1, pt: theme.spacing(6) })}>
                <FormSearchSelection
                  name='startYear'
                  placeholder='เลือก Year'
                  options={AVAILABLE_YEARS_OPTIONS}
                />
              </Box>
            </Flex>
            <Text sx={(theme) => ({ whiteSpace: 'nowrap', pt: theme.spacing(8) })}>to</Text>
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(2),
                alignItems: 'flex-start',
                flexBasis: '408px',
              })}
            >
              <Box sx={{ flex: 1 }}>
                <FormSearchSelection
                  name='endMonth'
                  label='End Month'
                  placeholder='เลือก Month'
                  options={MONTH_OPTIONS}
                />
              </Box>
              <Box sx={(theme) => ({ flex: 1, pt: theme.spacing(6) })}>
                <FormSearchSelection
                  name='endYear'
                  placeholder='เลือก Year'
                  options={AVAILABLE_YEARS_OPTIONS}
                />
              </Box>
            </Flex>
          </Flex>
        </Grid>
        <ItemAnalysisOtherFilterFormField
          questionStatusOptions={QuestionStatusOptions}
          disableKnowledgeGroupCustomThreshold={disableKnowledgeGroupCustomThreshold}
          disableNonKnowledgeGroupCustomThreshold={disableNonKnowledgeGroupCustomThreshold}
          qBankStructuresOptions={qBankStructuresOptions}
          questionStatusSelected={formContext.getValues('questionStatus').map((item) => item.value)}
          getAllQBankStructuresLoading={getAllQBankStructuresLoading}
          showKnowledgeGroupThresholdError={
            !!formContext.formState.errors?.bIndex?.knowledgeGroup?.customThreshold
          }
          showNonKnowledgeGroupThresholdError={
            !!formContext.formState.errors?.bIndex?.nonKnowledgeGroup?.customThreshold
          }
        />
        <Divider />
        <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
          <Button color='secondary' variant='outlined' onClick={() => handleReset()}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
